import React, { useId } from 'react';
import { AbsoluteFill, useVideoConfig } from 'remotion';
import { useAnimeTimeline } from '../useAnimeTimeline';
import { createTimeline, stagger, text } from 'animejs';
import { TextItem } from './TextBlock';

export const FancyTextBlock: React.FC<{
	textItem: TextItem;
}> = ({ textItem }) => {
	const { durationInFrames } = useVideoConfig();
	const uniqueId = useId().replace(/:/g, '_');
	const textElementId = `text-to-split-${uniqueId}`;

	const processTextContent = (content: string) => {
		return content
			.replace(/&amp;/g, '&')
			.replace(/&lt;/g, '<')
			.replace(/&gt;/g, '>')
			.replace(/&quot;/g, '"')
			.replace(/<br\s*\/?>/gi, '\n');
	};

	const processedContent = processTextContent(textItem.content);
	console.log('#log processedContent', { processedContent, content: textItem.content });
	const resolvedStyle = (textItem.style ?? textItem.conf?.type ?? '').toLowerCase();
	const resolvedColorScheme = (textItem.colorScheme ?? 'dark').toLowerCase();
	const isTerminBox = resolvedStyle === 'terminbox';
	const isTikTokStyle = resolvedStyle === 'tiktok';
	const isLightScheme = resolvedColorScheme === 'light';
	const textColor = isLightScheme ? '#101317' : 'white';
	const overlayGradient = isLightScheme
		? 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.78))'
		: 'linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.7))';
	const tiktokWordBackgroundColor = isLightScheme ? 'rgba(255, 255, 255, 0.94)' : 'rgba(0, 0, 0, 0.88)';
	const tiktokWordBorderColor = isLightScheme ? 'rgba(16, 19, 23, 0.22)' : 'rgba(255, 255, 255, 0.12)';
	const tiktokLines = processedContent
		.split('\n')
		.filter((line) => line.trim().length > 0);

	const tiktokWordsByLine = (tiktokLines.length > 0 ? tiktokLines : [processedContent])
		.map((line) => line.trim().split(/\s+/).filter((word) => word.length > 0))
		.filter((lineWords) => lineWords.length > 0);

	const scopeRef = useAnimeTimeline(() => {
		console.log('#log useAnimeTimeline called', { durationInFrames });

		const totalTime = durationInFrames * (1000 / 30);
		const tl = createTimeline({
			defaults: {
				loop: false,
				ease: 'inOut(3)',
				duration: totalTime,
			},
		});

		if (isTikTokStyle) {
			const lineElements = Array.from(
				document.querySelectorAll<HTMLElement>(
					`#${textElementId} [data-tiktok-line-bg="true"]`
				)
			);

			const wordElements = Array.from(
				document.querySelectorAll<HTMLElement>(
					`#${textElementId} [data-tiktok-word="true"]`
				)
			);

			if (wordElements.length === 0) {
				return tl;
			}

			const timePerWord = Math.min((totalTime * 0.6) / wordElements.length, 500);
			let cumulativeWordIndex = 0;

			tl
				.add(wordElements, {
					opacity: [0, 1],
					duration: 350,
					delay: stagger(timePerWord),
				}, 0);

			for (let lineIndex = 0; lineIndex < lineElements.length; lineIndex++) {
				const lineStartMs = Math.max(cumulativeWordIndex * timePerWord - 40, 0);
				tl.add(lineElements[lineIndex], {
					opacity: [0, 1],
					duration: 220,
				}, lineStartMs);

				cumulativeWordIndex += tiktokWordsByLine[lineIndex]?.length ?? 0;
			}

			tl.init();

			return tl;
		}

		const { words } = text.split(`#${textElementId}`, {
			words: { wrap: 'clip' },
			chars: false,
		});

		if (words.length === 0) {
			return tl;
		}

		const timePerWord = Math.min((totalTime * 0.6) / words.length, 500);
		console.log('#log 5914ords', { timePerWord });

		tl
			.add(words, {
				opacity: [0, 1],
				duration: 500,
			}, stagger(timePerWord))
			.init();

		return tl;
	});

	const renderContent = () => {
		if (!isTikTokStyle) {
			return <div id={textElementId}>{processedContent}</div>;
		}

		return (
			<div
				id={textElementId}
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-start',
					gap: '0.28em',
				}}
			>
				{tiktokWordsByLine.map((lineWords, lineIndex) => (
					<div
						key={`${textElementId}-line-${lineIndex}`}
						style={{
							display: 'inline-flex',
							flexWrap: 'wrap',
							gap: '0.24em',
							opacity: 0,
							backgroundColor: tiktokWordBackgroundColor,
							padding: '0.17em 0.5em 0.22em 0.5em',
							borderRadius: '0.34em',
							border: `1px solid ${tiktokWordBorderColor}`,
						}}
						data-tiktok-line-bg="true"
					>
						{lineWords.map((word, wordIndex) => (
							<span
								key={`${textElementId}-word-${lineIndex}-${wordIndex}`}
								style={{
									display: 'inline-block',
									opacity: 0,
									fontWeight: 700,
									letterSpacing: '0.01em',
								}}
								data-tiktok-word="true"
							>
								{word}
							</span>
						))}
					</div>
				))}
			</div>
		);
	};

	return (
		<AbsoluteFill
			style={{
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'flex-end',
				justifyContent: 'center',
				zIndex: 2,
			}}
			id={scopeRef}
		>
			<div
				style={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					width: '100%',
					height: '50%',
					background: overlayGradient,
					zIndex: 1,
				}}
			/>
			<div style={{
				color: textColor,
				fontSize: isTerminBox ? '1rem' : isTikTokStyle ? '0.82rem' : '0.7rem',
				fontFamily: 'Grift',
				textAlign: 'left',
				padding: '1rem',
				lineHeight: isTikTokStyle ? 1.25 : 1.4,
				textShadow: isTikTokStyle || isLightScheme ? 'none' : '2px 2px 4px rgba(0,0,0,0.5)',
				whiteSpace: 'pre-line',
				zIndex: 2,
				paddingBottom: isTikTokStyle ? '22%' : '25%',
			}}>
				{renderContent()}
			</div>
		</AbsoluteFill>
	);
};

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
